<template>
  <img :src="propsStore.logo" class="relative -top-16 mx-auto" width="400px" />
  <div class="flex flex-row justify-center align-middle">
    <Card
      class="p-4 sm:basis-full md:basis-2/3 xl:basis-1/2 2xl:basis-1/3 rounded-xl"
      pt:root:class=" isolate bg-white/80! backdrop-blur-xl dark:bg-surface-800/90! border-surface-200! dark:border-surface-600! border!"
      :pt-options="{ mergeProps: false }"
    >
      <template #title>
        <div class="relative text-center -inset-y-12 -m-8">
          <span class="bg-primary-500 dark:bg-primary-400 inline-flex justify-center items-center rounded-full drop-shadow-xl h-20 w-20">
            <i class="pi pi-lock text-4xl text-white dark:text-surface-800"></i>
          </span>
        </div>
      </template>
      <template #content>
        <IconField iconPosition="left" class="mb-6 w-full">
          <InputIcon>
            <i class="pi pi-user relative -top-1 text-2xl"></i>
          </InputIcon>
          <InputText autofocus placeholder="Username" :disabled="ssoEnabled" v-model="userVal" type="text" class="w-full text-2xl" />
        </IconField>
        <Password
          placeholder="Password"
          v-model="passVal"
          :disabled="ssoEnabled"
          v-on:keyup.enter="loginClick"
          :feedback="false"
          toggle-mask
          class="w-full mb-6"
          inputClass="w-full text-2xl"
        />
        <Button
          :label="ssoEnabled ? 'Login With SSO' : adminLogin ? 'Login' : 'Login To Portal'"
          class="w-full text-2xl"
          raised
          @click="loginClick"
        ></Button>
        <div v-if="!ssoEnabled" class="text-center pt-5 text-lg">
          <Button label="Forgot Password" class="px-10" text @click="$emit('navigate', 'PASSWORD')"></Button>
          <Button label="Forgot Username" class="px-10" text @click="$emit('navigate', 'USERNAME')"></Button>
        </div>
        <!-- <div class="text-center -mb-10 text-sm">
          <Button v-if="adminLogin" label="Go To Portal Login" class="px-10" text @click="changeLogin"></Button>
          <Button v-else label="Go To Admin Login" class="px-10" text @click="changeLogin"></Button>
        </div> -->
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import type { Tokens } from "@/types/Token";
import { useAuthStore } from "@/stores/auth";
import { AxiosError } from "axios";
import { usePropsStore } from "@/stores/props";
import type { SSOService } from "@/services/SSOService";

const router = useRouter();
const userVal = ref("");
const passVal = ref("");
const authStore = useAuthStore();
const propsStore = usePropsStore();

const sso: SSOService | undefined = inject("sso");

const emit = defineEmits<{
  navigate: [to: "LOGIN" | "PASSWORD" | "USERNAME"];
}>();

interface Props {
  adminLogin: boolean;
}
const props = defineProps<Props>();

const ssoEnabled = computed<boolean>(() => propsStore.ssoEnabled && props.adminLogin);

//TODO Maybe bring back the login swap between portal and admin for dev use only?

const loginClick = (): void => {
  if (propsStore.ssoEnabled) ssoLogin();
  else login();
};

const ssoLogin = (): void => {
  if (sso) sso.login();
};

const login = (): void => {
  const loginString = props.adminLogin ? "/admin-login" : "/portal-login";
  authStore.sessionExpired = false;
  axios
    .post(
      loginString,
      {},
      {
        auth: {
          username: userVal.value,
          password: passVal.value,
        },
      }
    )
    .then((response) => {
      handleApiResponse(
        response,
        (data: Tokens) => {
          const token = data;
          authStore.token = token.accessToken;
          authStore.refreshToken = token.refreshToken;
          authStore.refreshing = false;
          authStore.sessionExpired = false;
          authStore.forcePasswordChange = token.forcePasswordChange;
          const userInfo = authStore.getUserInfo();
          info("Welcome", userInfo.firstName + " " + userInfo.lastName);

          if (authStore.forcePasswordChange) {
            router.push({
              name: props.adminLogin ? "change-pass" : "portal-change-pass",
            });
          } else if (props.adminLogin) {
            if (propsStore.streamline) {
              router.push({
                name: "project-list",
              });
            } else {
              router.push({
                name: "admin-dashboard",
              });
            }
          } else {
            router.push({
              name: "portal-project-list",
            });
          }
        },
        (errors: ApiError[]) => {
          error("Login Failed", errors[0].message);
        }
      );
    })
    .catch((e: AxiosError) => {
      if (e.response && e.response.status === 401) errorApi(e, "Login Failed", "Invalid username and password");
      else if (e.response && e.response.status === 423) errorApi(e, "Login Failed", "User account is locked, please contact an admin");
      else errorApi(e);
    });
};

const changeLogin = (): void => {
  if (props.adminLogin) {
    router.push({
      name: "portal-login",
    });
  } else {
    router.push({
      name: "admin-login",
    });
  }
};
</script>
